import React, { FC, useCallback, useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { SumsubWebSdkWrapper } from "./SumsubWebSdkWrapper";
import { selectCurrentUser } from "../../../store/reducers/user";
import {
  useGetKycUrlMutation,
  useGetUserQuery,
} from "../../../store/services/user";
import { KYCStatus } from "../../../types/UserTypes";
import { errorsFromResponse } from "../../../utils/errors";

type Props = {
  callback: (status: KYCStatus) => void;
};

const MAX_RETRIES = 3;

export const KycForm: FC<Props> = ({ callback }) => {
  const [check, setCheck] = useState(false);
  const [retries, setRetries] = useState(0);
  const [timer, setTimer] = useState<NodeJS.Timer | undefined>(undefined);
  const [getKycToken, { data, isError, error, reset }] = useGetKycUrlMutation();
  const { refetch } = useGetUserQuery();

  const { email, phone, kyc_complete, kyc_complete_and_approved } =
    useSelector(selectCurrentUser);

  useEffect(() => {
    // Reset request on unmount to get new token on each mount (like clear cache)
    return () => reset();
  }, [reset]);

  useEffect(() => {
    if (!data) {
      getKycToken();
    }
  }, [data, getKycToken]);

  useEffect(() => {
    if (check) {
      const interval = setInterval(
        () => setRetries((current) => current + 1),
        1000
      );

      setTimer(interval);

      return () => clearInterval(interval);
    }
  }, [check, refetch]);

  useEffect(() => {
    if (retries > 0) {
      refetch();

      if (retries >= MAX_RETRIES) {
        setCheck(false);
        clearInterval(timer);
      }
    }
  }, [refetch, retries, timer]);

  useEffect(() => {
    if (retries > 0 && kyc_complete) {
      callback(
        kyc_complete_and_approved ? KYCStatus.VERIFIED : KYCStatus.SUSPECTED
      );
    }
  }, [kyc_complete, callback, retries, kyc_complete_and_approved]);

  const onStatusChanged = useCallback(
    (result: any) => {
      if (result && result.idDocSetType === "IDENTITY") {
        callback(KYCStatus.STARTED);
        return;
      }

      if (
        ["pending", "onHold"].includes(result.reviewStatus) ||
        result.reviewResult?.reviewAnswer === "GREEN" ||
        result.reviewResult?.reviewRejectType === "FINAL"
      ) {
        setCheck(true);
      }
    },
    [callback]
  );

  return (
    <SumsubWebSdkWrapper
      token={data?.token || ""}
      email={email}
      phone={phone}
      errors={isError ? errorsFromResponse(error) : []}
      onStatusChanged={onStatusChanged}
    />
  );
};
